import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  PaymentDayChangePreview,
  previewPaymentDayChange,
  updatePaymentDay,
} from '../../../services/request/DynamicInterest';
import { formatCurrency } from '../../../utils/CurrencyFormat';

interface Props {
  open: boolean;
  debitId: string | null;
  existingPaymentDay: number;
  onClose: () => void;
  onUpdated: () => void;
}

const PaymentDayChangeDialog: React.FC<Props> = ({
  open,
  debitId,
  existingPaymentDay,
  onClose,
  onUpdated,
}) => {
  const [newDay, setNewDay] = useState(String(existingPaymentDay || 1));
  const [preview, setPreview] = useState<PaymentDayChangePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNewDay(String(existingPaymentDay || 1));
      setPreview(null);
      setError(null);
    }
  }, [open, existingPaymentDay]);

  useEffect(() => {
    if (!open || !debitId) return;
    const day = parseInt(newDay, 10);
    if (Number.isNaN(day) || day < 1 || day > 31) {
      setPreview(null);
      return;
    }
    if (day === existingPaymentDay) {
      setPreview(null);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const p = await previewPaymentDayChange(debitId, day);
        setPreview(p);
      } catch (e: any) {
        setError(e?.message || 'Failed to load preview');
        setPreview(null);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [newDay, open, debitId, existingPaymentDay]);

  const handleConfirm = async () => {
    if (!debitId) return;
    const day = parseInt(newDay, 10);
    if (Number.isNaN(day) || day < 1 || day > 31) {
      setError('Enter a valid day between 1 and 31');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await updatePaymentDay(debitId, day);
      onUpdated();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update payment day</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" paragraph>
          Changing the pay day adjusts when the next interest cycle starts. Review the impact before confirming.
        </Typography>
        <Box display="flex" gap={2} alignItems="center" mb={2}>
          <TextField
            label="Existing pay day"
            value={existingPaymentDay}
            disabled
            size="small"
            sx={{ width: 140 }}
          />
          <TextField
            label="New pay day (1–31)"
            type="number"
            value={newDay}
            onChange={(e) => setNewDay(e.target.value)}
            size="small"
            inputProps={{ min: 1, max: 31 }}
            sx={{ width: 160 }}
          />
        </Box>
        {loading && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {preview && !loading && (
          <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Impact preview</Typography>
            <Typography variant="body2">Days difference: {preview.dayDifference > 0 ? '+' : ''}{preview.dayDifference}</Typography>
            <Typography variant="body2">
              Current pending interest: {formatCurrency(preview.currentPendingInterest)}
            </Typography>
            <Typography variant="body2">
              After change: {formatCurrency(preview.projectedPendingInterest)}
              {' '}
              ({preview.pendingInterestDelta >= 0 ? '+' : ''}{formatCurrency(Math.abs(preview.pendingInterestDelta))})
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Elapsed days: {preview.currentElapsedDays} → {preview.projectedElapsedDays}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2">{preview.cycleImpactSummary}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {preview.partialAdjustmentNote}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting || !preview || parseInt(newDay, 10) === existingPaymentDay}
        >
          {submitting ? 'Updating…' : 'Confirm update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDayChangeDialog;
